import React, {  } from 'react';
import styled, { StylesProps, cx } from '../../../lib/styles';
import storySectionStyles from './StoriesBlock_styles';
import { Article, Author } from '../../../models/article';
import Ad, { Ad as AdData } from '../Ad';
import Button from '../Button';
import Typography from '../Typography';
import ResponsiveRender from '../../../behaviour/ResponsiveRender';
import Story from '../Story/Story';
import HeavyStory from '../Story/HeavyStory';
import TeaserStory, { Teaser } from '../Story/TeaserStory';
import { Color } from '../../../lib/theme';
import Spacer from '../Spacer';
import { BaseBlockProps } from '../../../models/travelCollection';
import { ARTICLE_FEATUREDARTICLE_CLICK, SIGNUP_ARTICLE_COLLECTION_CHALLENGE, raiseEvent } from '../../../lib/gtm';
import MembershipChallenge from '../MembershipChallenge';
import _ from 'lodash';
import { compose } from 'recompose';
import { useSelector } from 'react-redux';
import { TrackingDataState } from '../../../store/reducers/trackingData';
import AuthorCallout from '../Callout/AuthorCallout';

interface StoriesBlockAside {
  slotKey?: string;
  title: string;
  articles: Article[];
  showMoreText?: string;
}

interface StoriesBlockTeaser extends Teaser {
  slotKey?: string;
}

const showAuthor = ({ id }) =>
  !!id &&
  id.toString() !== '129' &&
  id.toString() !== '208' &&
  id.toString() !== '50305';

export interface StoriesBlockData extends StoriesBlockProps {
  type: 'travel_article_block' | 'article_block';
}

export interface StoriesBlockProps extends BaseBlockProps {
  className?: string;
  name?: string;
  attribute?: boolean;
  heading?: string;
  subheading?: string;
  feature?: boolean;
  adIDSuffix?: string;
  hideAds?: boolean;
  stickyAd?: AdData;
  enterpriseAd?: AdData;
  topAd?: AdData;
  bottomAd?: AdData;
  recirculationAd1?: AdData;
  recirculationAd2?: AdData;
  hasMore?: boolean;
  hasMoreColor?: Color;
  amp?: boolean;
  articles: Article[];
  onClick?: (article: Article) => any;
  author?: Author;
  aside?: StoriesBlockAside;
  teaser?: StoriesBlockTeaser;
  onShowMore?: () => any;
  isLoading?: boolean;
  slotKey?: string;
  slotOffset?: number;
  lazy?: boolean;
  images?: boolean;
  backfillCount?: number;
  preferTrackingBackfill?: boolean;
  blockIndex: number;
}

const StoriesBlock = ({
  className,
  articles,
  author,
  //@ts-ignore
  aside,
  heading,
  subheading,
  attribute = true,
  feature,
  teaser,
  onClick,
  hasMore = false,
  hasMoreColor = 'light_grey',
  isLoading = false,
  lazy = true,
  images = true,
  amp,
  adIDSuffix,
  hideAds = false,
  stickyAd,
  enterpriseAd,
  topAd,
  bottomAd,
  recirculationAd1,
  recirculationAd2,
  slotKey,
  slotOffset = 0,
  onShowMore,
  styles,
  isV2Collection = false,
  //@ts-ignore
  blockIndex,
}: StoriesBlockProps & StylesProps<typeof storySectionStyles>) => {
  
  const trackingData = useSelector((state: any): TrackingDataState => (state.trackingData));
  const articlesAboveAdSeparator = articles.slice(feature ? 1 : 0, feature ? 6 : 5);
  const articlesBelowAdSeparator = articles.slice(feature ? 6 : 5);
  // console.log('StoriesBlock ads', { hideAds, stickyAd, enterpriseAd, topAd, bottomAd, recirculationAd1, recirculationAd2 });

  return (
    <div className={cx('StoriesBlock', styles.container, className, (isV2Collection ? `v2-collection-block` : null))}>

      {(heading || subheading) && (
        <div className={styles.headingContainer}>
          {heading && (
            <Typography>
              <h1>{heading}</h1>
            </Typography>
          )}

          {subheading && (
            <Typography weight={'normal'}>
              <h3>{subheading}</h3>
            </Typography>
          )}
        </div>
      )}

      <div className={styles.articles}>
        <div className={styles.articlesLeft}>
          <div className={`${styles.articlesList} articlesList`}>
            {articles.length > 0 &&
              (feature ? (
                <HeavyStory
                  slot={slotKey && `${slotKey}-slot-${slotOffset + 1}`}
                  attribute={attribute && showAuthor(articles[0].author)}
                  article={articles[0]}
                  onClick={() => {
                    raiseEvent(ARTICLE_FEATUREDARTICLE_CLICK);
                    return onClick && onClick(articles[0]);
                  }}
                  lazy={lazy}
                  image={images}
                />
              ) : (
                <Story
                  amp={amp}
                  slot={slotKey && `${slotKey}-slot-${slotOffset + 1}`}
                  attribute={attribute && showAuthor(articles[0].author)}
                  article={articles[0]}
                  onClick={() => onClick && onClick(articles[0])}
                  lazy={lazy}
                  image={images}
                />
              ))}

              {articlesAboveAdSeparator.map((article: Article, i: number) => (
                <React.Fragment key={i}>
                  {i === 2 && recirculationAd1 && (
                    <Ad
                      className={styles.recirculationAd}
                      ad={recirculationAd1}
                      adIDSuffix={adIDSuffix}
                      gutter={'s'}
                      // backgroundColor="transparent"
                      lazy
                      initialSize={['100%', 0]}
                    />
                  )}
                  <Story
                    amp={amp}
                    slot={slotKey && `${slotKey}-slot-${slotOffset + i + 2}`}
                    attribute={!author && attribute && showAuthor(article.author)}
                    article={article}
                    onClick={() => onClick && onClick(article)}
                    lazy={lazy}
                    image={images}
                  />
                </React.Fragment>
              ))}
          </div>
        </div>

        {!hideAds && (
          <ResponsiveRender match={'l'} up>
            <div className={`${styles.asideContainer} full-height`}>
              {(author && author.description) ? (
                  <AuthorCallout author={author} />
                ) :
                (
                  <Spacer space={'m'} selfH={'xs'} selfT={0} align={'center'} className={`taboola-feed first-aside`}>
                    {enterpriseAd && (
                      <Ad
                        ad={enterpriseAd}
                        adIDSuffix={adIDSuffix}
                        initialSize={[320, 50]}
                        backgroundColor={'transparent'}
                      />
                    )}
                  </Spacer>
                )
              }
            </div>
          </ResponsiveRender>
        )}
      </div>

      {topAd && (
        <Ad
          ad={topAd}
          adIDSuffix={adIDSuffix}
          lazy
          gutter={false}
          initialSize={[300, 90]} 
          backgroundColor='transparent'
        />
      )}

      <div className={styles.articles}>
        <div className={styles.articlesLeft}>
          <div className={styles.articlesList}>
            {articlesBelowAdSeparator.map((article: Article, i: number) => (
              <React.Fragment key={i}>
                {i === 2 && recirculationAd2 && (
                  <Ad
                    className={styles.recirculationAd}
                    ad={recirculationAd2}
                    adIDSuffix={adIDSuffix}
                    gutter={'s'}
                    // backgroundColor="transparent"
                    lazy
                    initialSize={['100%', 0]}
                  />
                )}
                {i === 4 && trackingData.isLoaded && !trackingData.email && (
                  <MembershipChallenge
                    onClick={() => {
                      raiseEvent(SIGNUP_ARTICLE_COLLECTION_CHALLENGE);
                    }}
                  />
                )}
                {i === 8 && enterpriseAd && (
                  <ResponsiveRender match={'l'} down>
                    <Ad
                      ad={enterpriseAd}
                      adIDSuffix={adIDSuffix}
                      lazy
                      initialSize={[320, 50]}
                    />
                  </ResponsiveRender>
                )}
                <Story
                  amp={amp}
                  slot={slotKey && `${slotKey}-slot-${slotOffset + i + 2}`}
                  attribute={!author && attribute && showAuthor(article.author)}
                  article={article}
                  onClick={() => onClick && onClick(article)}
                  lazy={lazy}
                  image={images}
                />
              </React.Fragment>
            ))}
            
            {stickyAd && (  //  position stickyAd as an MRec within the article flow on small screens
              <ResponsiveRender match={'l'} down>
                <Ad
                  ad={stickyAd}
                  adIDSuffix={adIDSuffix}
                  lazy
                  initialSize={[320, 50]}
                />
              </ResponsiveRender>
            )}

            {hasMore && (
              <Spacer align={'center'}>
                <Button
                  color={hasMoreColor}
                  onClick={() => !isLoading && onShowMore && onShowMore()}
                  disabled={isLoading}
                  className={isLoading ? styles.loadingButton : ''}
                >
                  {isLoading && <div className={styles.loading} />}
                  {!isLoading && 'Show me more stories'}
                </Button>
              </Spacer>
            )}
          </div>
        </div>

        <div className={styles.asideContainer}>
          <ResponsiveRender match={'l'} up>
            <div className={styles.adContainer}>
              {stickyAd && (
                <Ad
                  ad={stickyAd}
                  adIDSuffix={adIDSuffix}
                  initialSize={[300, 600]}
                  lazy
                  sticky
                  backgroundColor={'transparent'}
                />
              )}
            </div>
          </ResponsiveRender>
        </div>
      </div>

      {teaser && (
        <TeaserStory
          slot={teaser.slotKey && `${teaser.slotKey}-slot-1`}
          {...teaser}
        />
      )}

      {bottomAd && (
        <Ad
          ad={bottomAd}
          adIDSuffix={adIDSuffix}
          lazy
          initialSize={[300, 90]} 
          backgroundColor={'transparent'}
        />
      )}
    </div>
  );
}

export default compose(
  styled(storySectionStyles)
)(StoriesBlock);